/**
 * Pure timesheet policy: which fichada minutes belong to which calendar
 * date, and which fichadas are anomalies that must block payroll settlement
 * until an admin reviews them (timesheet-audit spec, "Anomaly Detection" +
 * "Midnight-Crossing Shift Date Attribution"; design.md "Payroll policy").
 *
 * No I/O, no Supabase, no DOM — same split as
 * `js/shared/mesas-policy.js`/`admin-reservations-policy.js`.
 *
 * PARITY DUPLICATE — read before editing either copy:
 * This module has an identical twin at `api/_lib/timesheet-policy.js`. Both
 * copies MUST produce byte-for-byte identical output for the same input
 * (enforced by `tests/unit/timesheet-policy-parity.test.js`). This is
 * intentional duplication, not accidental drift waiting to happen: Vercel's
 * serverless bundler (`@vercel/nft`) traces each `api/*.js` entrypoint's
 * dependency graph independently and does not reliably follow an import
 * that crosses out of the `api/` tree (e.g. `../../js/shared/...`). Such an
 * import can resolve fine in local dev (Vite serves the whole repo from one
 * process) and then fail at runtime in production with a module-not-found
 * error once Vercel packages only what it traced for that function. Until
 * this project restructures around a real shared package, `js/shared/`
 * stays client-only and `api/_lib/` stays server-only, with the parity test
 * as the single source of truth that both copies agree. See
 * `design-amendment-main-branch.md` ("Parity modules") for the decision —
 * the precedent file that would normally document this convention does not
 * exist in this tree yet, so the rationale is written out here in full.
 *
 * Timezone: Argentina (ART) is a FIXED GMT-3 offset with no daylight saving
 * time, so every "local calendar date" computation below uses plain
 * millisecond offset arithmetic (`ART_OFFSET_MINUTES`) instead of
 * `Intl.DateTimeFormat`/a timezone database. NO hay precedente de esta
 * convencion en este arbol: es la primera vez que se hace aritmetica de
 * offset aca, asi que no se cita ningun archivo previo (una cita a un
 * precedente inexistente es peor que no citar nada — manda al lector a
 * buscar algo que no va a encontrar).
 *
 * The trick: shift a real UTC instant back by the ART offset, then read
 * UTC calendar fields (`getUTCFullYear`/`getUTCMonth`/`getUTCDate`) off the
 * SHIFTED instant — that gives the ART wall-clock date for the ORIGINAL
 * instant without ever touching the host machine's timezone.
 */

/** Argentina Time offset from UTC, in minutes. Fixed — no DST to account for. */
export const ART_OFFSET_MINUTES = 180;

/** A CLOSED shift longer than this many minutes is a duration anomaly. Never used to truncate. */
export const ANOMALY_MAX_SHIFT_MINUTES = 16 * 60;

/**
 * An OPEN shift that has been open MORE than this many minutes is an anomaly
 * (owner decision 2026-09-27). Replaces the old "entrada before today ART"
 * rule, which flagged a night shift still in progress right after midnight.
 */
export const ANOMALY_MAX_OPEN_MINUTES = 12 * 60;

/**
 * Anomaly type tags returned by {@link detectAnomalies}. `ABIERTA` keeps its
 * historical wire value ('abierta_dia_anterior') even though the rule is now
 * "open more than 12 h": it travels in `api/liquidacion.js`'s 409
 * `anomalias_pendientes` body, so it is a stable identifier, not UI copy.
 */
export const ANOMALY_TYPES = {
    ABIERTA: 'abierta_dia_anterior',
    DURACION_EXCESIVA: 'duracion_excesiva',
    INVERTIDA: 'invertida',
};

/**
 * Kiosk clock-action literals (time-clock-kiosk spec, "PIN-Validated
 * Server-Stamped Clock Action"). Single source of truth for the
 * `'entrada'`/`'salida'` strings shared by `api/kiosco.js` (POST body
 * `accion` contract), `js/kiosco.js` (dialog intent + POST payload) and
 * `PinPad.js` (dialog title) — previously three independent literal copies
 * (Kiosk round 2 follow-up, review-47bbda686416eead finding 6). Frozen so a
 * caller cannot mutate one copy and silently change what the rest of the
 * kiosk accepts.
 */
export const ACCIONES = Object.freeze({
    ENTRADA: 'entrada',
    SALIDA: 'salida',
});

/**
 * Longitud máxima (en caracteres) de un `motivo` de anulación de liquidación
 * (Unit B2, review-5b84ba2ca338f75a follow-up — el campo no tenía cota).
 * Compartida entre `admin-liquidacion.service.js` (validación client-side +
 * `maxlength` del `<textarea>`) y `api/liquidacion.js` (`parsePayload`, 400
 * `motivo_demasiado_largo`) — un solo valor, mismo criterio que el resto de
 * las constantes de este módulo (`payroll-settlement-anulacion`, no
 * `timesheet-audit`, pero vive acá porque `motivo` ya es un concepto de
 * auditoría compartido con `admin_edit_fichada`/`FichadaEditDialog`, y este
 * es el módulo de políticas ya duplicado en paridad client/server).
 */
export const MOTIVO_MAX_LEN = 500;

/**
 * @param {string | number | Date} value
 * @returns {number} epoch milliseconds
 * @throws {TypeError} si el valor no es una fecha parseable
 */
function toMs(value) {
    // `null` se rechaza explicito: `new Date(null)` NO es invalido, es el epoch
    // (1970-01-01). Sin esta linea, un entrada_at nulo se convertiria en una
    // fecha plausible pero falsa en vez de fallar — la misma clase de error
    // silencioso que la guarda de abajo evita, entrando por otra puerta.
    // (salida_at nulo es legitimo: significa jornada abierta, y quien llama lo
    // chequea ANTES de pasar por aca.)
    if (value == null) {
        throw new TypeError('timesheet-policy: timestamp ausente');
    }
    const ms = new Date(value).getTime();
    // Falla RUIDOSA a proposito. Si devolvieramos NaN, cada comparacion contra
    // el daria false en JS, asi que la jornada saldria con cero minutos Y sin
    // ninguna anomalia: desapareceria a la vez del sueldo y de la red que
    // justamente existe para frenar la liquidacion ante datos malos. Un sueldo
    // silenciosamente incompleto es mucho peor que una excepcion visible.
    if (Number.isNaN(ms)) {
        throw new TypeError('timesheet-policy: timestamp invalido');
    }
    return ms;
}

/**
 * Shifts a real UTC instant back by the ART offset. Reading UTC calendar
 * fields off the RESULT yields ART wall-clock fields for the ORIGINAL
 * instant — no Intl/timezone lookup involved.
 * @param {number} utcMs
 * @returns {number}
 */
function shiftToArt(utcMs) {
    return utcMs - ART_OFFSET_MINUTES * 60000;
}

/**
 * ART calendar date (`YYYY-MM-DD`) of a real UTC instant.
 * @param {number} utcMs
 * @returns {string}
 */
export function artDateKey(utcMs) {
    const shifted = new Date(shiftToArt(utcMs));
    const year = shifted.getUTCFullYear();
    const month = String(shifted.getUTCMonth() + 1).padStart(2, '0');
    const day = String(shifted.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * D2 (owner decision 2026-09-27) — whether a settlement period ending on
 * `hasta` has already ENDED at `nowMs`: only days that already ended are
 * settled, so a period ending TODAY (ART) has not ended yet. "Today" is the
 * ART calendar date of `nowMs` ({@link artDateKey}), never the host's local
 * or UTC date (both are already tomorrow from 21:00 ART on). `api/liquidacion.js`
 * refuses `confirm` with it (400 `periodo_no_terminado`); the Liquidación
 * tablero uses the client copy to disable "Liquidar". PARITY DUPLICATE.
 * @param {string} hasta last ART calendar date of the period, `YYYY-MM-DD`
 * @param {number} nowMs epoch milliseconds
 * @returns {boolean}
 */
export function periodoTerminado(hasta, nowMs) {
    return hasta < artDateKey(nowMs);
}

/**
 * Real UTC instant of the NEXT ART local midnight strictly after `utcMs`
 * (i.e. the end of `utcMs`'s ART calendar day).
 * @param {number} utcMs
 * @returns {number}
 */
function nextArtMidnightUtcMs(utcMs) {
    const shifted = new Date(shiftToArt(utcMs));
    const nextShiftedMidnight = Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate() + 1);
    return nextShiftedMidnight + ART_OFFSET_MINUTES * 60000;
}

/**
 * Real UTC instant (ISO 8601) of ART local midnight at the START of the ART
 * calendar date `fechaYYYYMMDD` shifted by `deltaDays` whole days — the one
 * conversion every `fichadas.entrada_at` read window must use when it is
 * built from ART calendar dates (owner requirement: an employee can clock in
 * until 23:59 ART and every fichada of a day counts for that day).
 *
 * `${fecha}T00:00:00Z` (or a bare `'YYYY-MM-DD'` cast to `timestamptz` by a
 * UTC database session) is NOT the start of that ART day: it is 21:00 ART of
 * the PREVIOUS day. Used as an upper bound it silently drops every shift that
 * starts 21:00-23:59 ART on the last day of a range. ART is a fixed UTC-3
 * with no DST, so ART midnight of `D` is always UTC `D`T03:00:00Z.
 *
 * Throws on anything that is not a real `YYYY-MM-DD` calendar date or a
 * non-integer `deltaDays` — a malformed bound must fail loudly, never turn
 * into a plausible-but-wrong query window.
 * @param {string} fechaYYYYMMDD ART calendar date
 * @param {number} [deltaDays] whole ART days to shift (negative = earlier)
 * @returns {string} e.g. `artMidnightUtcIso('2026-09-26', 1)` -> `'2026-09-27T03:00:00.000Z'`
 * @throws {TypeError}
 */
export function artMidnightUtcIso(fechaYYYYMMDD, deltaDays = 0) {
    const match = typeof fechaYYYYMMDD === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(fechaYYYYMMDD) : null;
    if (!match) {
        throw new TypeError('timesheet-policy: se esperaba una fecha YYYY-MM-DD');
    }
    if (!Number.isInteger(deltaDays)) {
        throw new TypeError('timesheet-policy: deltaDays tiene que ser un entero');
    }
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const utcMidnight = Date.UTC(year, month - 1, day);
    // Date.UTC rolls an impossible date over (2026-02-30 -> 2026-03-02,
    // 2026-13-01 -> 2027-01-01); reject it instead of querying a day the
    // caller never asked for.
    const roundTrip = new Date(utcMidnight);
    if (roundTrip.getUTCMonth() !== month - 1 || roundTrip.getUTCDate() !== day) {
        throw new TypeError('timesheet-policy: se esperaba una fecha YYYY-MM-DD');
    }
    const shiftedMidnight = Date.UTC(year, month - 1, day + deltaDays);
    return new Date(shiftedMidnight + ART_OFFSET_MINUTES * 60000).toISOString();
}

/**
 * `YYYY-MM-DD` shape AND a real calendar date (`2026-02-30` is not). The
 * calendar rule itself is `artMidnightUtcIso`'s, reused here, never a second
 * copy. Endpoints use it to answer 400 for an impossible date instead of a
 * 500 when Postgres later fails the cast.
 * @param {unknown} fecha
 * @returns {boolean}
 */
export function esFechaCalendarioReal(fecha) {
    try {
        artMidnightUtcIso(fecha);
        return true;
    } catch {
        return false;
    }
}

/**
 * Splits one fichada's minutes across the ART calendar date(s) it spans,
 * cutting exactly at ART local midnight (timesheet-audit spec,
 * "Midnight-Crossing Shift Date Attribution"). A shift is NEVER truncated
 * or capped here — a 17h shift still totals 17h across its buckets, it just
 * lands on two dates instead of one.
 *
 * A still-open shift (`salida_at` is `null`/`undefined`) has no known end,
 * so it cannot be split; this returns a single zero-minute bucket at the
 * entrada date. `detectAnomalies` is what flags an open shift as a problem —
 * this function only ever describes duration, never anomaly status.
 *
 * A non-positive duration (`salida_at <= entrada_at`, including an exact
 * zero-length shift) also collapses to a single zero-minute bucket at the
 * entrada date, for the same reason: this function has no basis to invent
 * negative or fabricated minutes. `detectAnomalies` is what flags that
 * condition (`ANOMALY_TYPES.INVERTIDA`), per the spec's literal
 * `salida_at <= entrada_at` rule.
 *
 * @param {{ entrada_at: string | number | Date, salida_at?: string | number | Date | null }} fichada
 * @returns {Array<{ fecha: string, minutos: number }>}
 */
export function splitMinutesByDate(fichada) {
    const entradaMs = toMs(fichada.entrada_at);

    if (fichada.salida_at == null) {
        return [{ fecha: artDateKey(entradaMs), minutos: 0 }];
    }

    const salidaMs = toMs(fichada.salida_at);
    if (salidaMs <= entradaMs) {
        return [{ fecha: artDateKey(entradaMs), minutos: 0 }];
    }

    const buckets = [];
    let cursor = entradaMs;
    while (cursor < salidaMs) {
        const boundary = Math.min(nextArtMidnightUtcMs(cursor), salidaMs);
        buckets.push({
            fecha: artDateKey(cursor),
            // Minutos EXACTOS, sin redondear. Redondear cada pedazo por
            // separado inventa o pierde minutos: el reloj de Postgres estampa
            // con precision de fracciones de segundo, asi que una jornada de
            // 300,03 minutos que cruza medianoche se partia en 240 + 61 = 301.
            // Un minuto de mas por turno nocturno es plata. El redondeo va UNA
            // sola vez al final, en el motor de liquidacion (design.md).
            minutos: (boundary - cursor) / 60000,
        });
        cursor = boundary;
    }
    return buckets;
}

/**
 * @param {string[]} fechas ART dates (`YYYY-MM-DD`) a fichada is attributed to
 * @param {string} [desde] inclusive lower bound
 * @param {string} [hasta] inclusive upper bound
 * @returns {boolean}
 */
function fechasEnRango(fechas, desde, hasta) {
    if (!desde && !hasta) return true;
    return fechas.some((fecha) => (!desde || fecha >= desde) && (!hasta || fecha <= hasta));
}

/**
 * Finds every fichada in `fichadas` that is a timesheet anomaly
 * (timesheet-audit spec, "Anomaly Detection"): still open after more than
 * `ANOMALY_MAX_OPEN_MINUTES` (12 h), a CLOSED shift over `ANOMALY_MAX_SHIFT_MINUTES`, or inverted
 * timestamps (`salida_at <= entrada_at`). An `anulada` fichada is never an
 * anomaly — an admin already voided it.
 *
 * Range-scoped by the ART date(s) each fichada is attributed to (via
 * {@link splitMinutesByDate} for closed shifts, or the entrada date for
 * open ones) — a shift that starts outside `[desde, hasta]` but crosses
 * into it still surfaces, per the spec's "Anomaly query is range-scoped and
 * reusable" scenario. Omitting both `desde` and `hasta` returns anomalies
 * across all of `fichadas` with no range filter.
 *
 * An open shift is judged ONLY by how long it has been open (`now - entrada`
 * strictly greater than `ANOMALY_MAX_OPEN_MINUTES`), never against
 * `ANOMALY_MAX_SHIFT_MINUTES` and never by its ART date: a night shift that
 * started 22:00 and is still running at 01:00 is not an anomaly.
 *
 * @param {Array<{ id: string, entrada_at: string | number | Date, salida_at?: string | number | Date | null, anulada?: boolean }>} fichadas
 * @param {{ now: string | number | Date, desde?: string, hasta?: string }} params
 * @returns {Array<{ fichadaId: string, tipo: string }>}
 */
export function detectAnomalies(fichadas, { now, desde, hasta } = {}) {
    const nowMs = toMs(now ?? new Date());
    const anomalies = [];

    for (const fichada of fichadas ?? []) {
        if (fichada.anulada) continue;

        const entradaMs = toMs(fichada.entrada_at);
        const abierta = fichada.salida_at == null;
        const fechas = abierta ? [artDateKey(entradaMs)] : splitMinutesByDate(fichada).map((bucket) => bucket.fecha);

        if (!fechasEnRango(fechas, desde, hasta)) continue;

        if (abierta) {
            if (nowMs - entradaMs > ANOMALY_MAX_OPEN_MINUTES * 60000) {
                anomalies.push({ fichadaId: fichada.id, tipo: ANOMALY_TYPES.ABIERTA });
            }
            continue;
        }

        const salidaMs = toMs(fichada.salida_at);
        if (salidaMs <= entradaMs) {
            anomalies.push({ fichadaId: fichada.id, tipo: ANOMALY_TYPES.INVERTIDA });
            continue;
        }

        if (salidaMs - entradaMs > ANOMALY_MAX_SHIFT_MINUTES * 60000) {
            anomalies.push({ fichadaId: fichada.id, tipo: ANOMALY_TYPES.DURACION_EXCESIVA });
        }
    }

    return anomalies;
}

/**
 * Open shifts (`salida_at` null, not `anulada`) that overlap a settlement
 * period ending on ART day `hasta` — F-F (owner decision 2026-09-27): a
 * settlement is never CONFIRMED while one exists, however few hours it has
 * been open. Under `ANOMALY_MAX_OPEN_MINUTES` an open shift is no anomaly and
 * `splitMinutesByDate` gives it 0 minutes, so it would be settled unpaid.
 *
 * An open shift runs from its entrada until now, so it overlaps the period
 * when it started before the ART midnight that closes `hasta`: one that
 * started inside the period AND one that started before `desde` (however long
 * ago) and is still open. One that started after `hasta`'s ART day (someone on
 * shift today while last week is settled) does not. So only `hasta` decides.
 *
 * This function only filters what it is GIVEN: the guarantee is only as wide
 * as the caller's read. Both callers therefore add the open shifts a padded
 * `desde - QUERY_PAD_DAYS` read would miss: `api/liquidacion.js` confirm runs
 * one extra read of open, non-anulada fichadas with no lower bound, and the
 * Liquidación tablero merges the open-shift banner's `listFichadasAbiertas`
 * read. The ONE rule for overlap: both call this same function (PARITY
 * DUPLICATE, see the file-level comment).
 *
 * Contrast with F-E (`rangoFechasJornada`, `js/shared/fichadas-ui-policy.js`,
 * and SQL `fichada_rango_art`): there an open shift touches only its entrada
 * date, because F-E asks which settled days an EDIT would change and an open
 * shift has no paid minutes yet; here it runs until now, because F-F asks
 * whether a later salida would add paid minutes to the period.
 * @param {Array<{ id: string, entrada_at: string | number | Date, salida_at?: string | number | Date | null, anulada?: boolean }>|null|undefined} fichadas
 * @param {{ hasta: string }} periodo `hasta`: last ART calendar date `YYYY-MM-DD` of the period
 * @returns {Array<object>} the matching fichadas, same objects, input order
 */
export function jornadasAbiertasEnPeriodo(fichadas, { hasta }) {
    const finMs = Date.parse(artMidnightUtcIso(hasta, 1));
    return (fichadas ?? []).filter((f) => !f.anulada && f.salida_at == null && toMs(f.entrada_at) < finMs);
}

/**
 * Formats how long an OPEN shift has been running, for the kiosk's presence
 * board (Kiosk UX follow-up, 2026-09-02; ticks every second since the Kiosk
 * UX round 2 follow-up, 2026-09-02). This is a client-clock display only —
 * the server's `entrada_at` timestamp is the source of truth, this function
 * just renders "how long ago" against whatever clock the browser is showing
 * right now. Not routed through `toMs` on purpose: a bad/missing
 * `entradaAtIso` here means "don't show anything" (the board just omits the
 * elapsed text), not the loud `TypeError` that payroll settlement needs —
 * this is decoration, not money.
 *
 * Seconds are ALWAYS zero-padded to 2 digits. Minutes are zero-padded to 2
 * digits ONLY once hours are shown — below one hour, minutes are printed as
 * their natural width (e.g. `"12m 03s"`, not `"012m 03s"`) since there is no
 * wider unit to align against.
 *
 * @param {string | number | Date | null | undefined} entradaAtIso
 * @param {number} nowMs client clock, epoch milliseconds
 * @returns {string} `"Ym Ss"` under one hour, `"Xh Ym Ss"` (Y/S zero-padded) at or above one hour, or `""` if `entradaAtIso` is missing/unparseable
 */
export function formatElapsed(entradaAtIso, nowMs) {
    if (entradaAtIso == null) return '';
    const entradaMs = new Date(entradaAtIso).getTime();
    if (Number.isNaN(entradaMs)) return '';

    // A future entrada_at (client clock briefly behind the server's) must
    // never render as a negative duration — clamp to zero instead.
    const elapsedMs = Math.max(0, nowMs - entradaMs);
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    if (hours === 0) return `${minutes}m ${seconds}s`;
    return `${hours}h ${String(minutes).padStart(2, '0')}m ${seconds}s`;
}

/**
 * Whether an OPEN shift that started at `entradaAtIso` has been open more
 * than {@link ANOMALY_MAX_OPEN_MINUTES} at `nowMs` — the kiosk's "¿Te
 * olvidaste de fichar la salida?" notice (F-B, owner decision 2026-09-27).
 * Same threshold and same strict `>` as `detectAnomalies`, so the kiosk card
 * and the admin's anomaly list flip at the same instant. Client-only (like
 * {@link formatElapsed}) and lenient on purpose: a missing/unparseable
 * entrada means "no notice", never a throw — this is decoration, not money.
 * @param {string | number | Date | null | undefined} entradaAtIso
 * @param {number} nowMs client clock, epoch milliseconds
 * @returns {boolean}
 */
export function jornadaAbiertaExcedida(entradaAtIso, nowMs) {
    if (entradaAtIso == null) return false;
    const entradaMs = new Date(entradaAtIso).getTime();
    if (Number.isNaN(entradaMs)) return false;
    return nowMs - entradaMs > ANOMALY_MAX_OPEN_MINUTES * 60000;
}
